import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { generateDTAI_ID, sendEncryptedMessage, listenToChat, processAIResponse } from '../services/chatService';
import { analyzeStats } from '../services/aiService';
import './AIAesthetics.css';

function DevTrackerAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  
  const location = useLocation();
  const { userProfile } = useAuth();

  const analysis = useMemo(() => {
    return userProfile?.stats ? analyzeStats(userProfile.stats) : null;
  }, [userProfile]);

  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(generateDTAI_ID());
    }
  }, [isOpen, sessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && sessionId && userProfile?.uid) {
      const unsubscribe = listenToChat(userProfile.uid, sessionId, (msgs) => {
        setMessages(msgs);
        scrollToBottom();
      });
      return () => unsubscribe();
    }
  }, [isOpen, sessionId, userProfile?.uid]);

  // Hide AI Assistant on Embed and Resume routes or if not logged in
  if (location.pathname.startsWith('/embed') || location.pathname.startsWith('/resume') || !userProfile) {
    return null;
  }


  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !userProfile?.uid || !sessionId) return;

    const userMsg = inputText.trim();
    setInputText('');
    
    // 1. Send encrypted user message
    await sendEncryptedMessage(userProfile.uid, sessionId, userMsg, 'user');
    scrollToBottom();

    // 2. Trigger AI Response Lifecycle
    setIsTyping(true);
    try {
      // Pass 'analysis' (DevTracker AI context) to the AI engine
      await processAIResponse(userProfile.uid, sessionId, userMsg, analysis);
    } catch (err) {
      console.error("AI Response failed", err);
    } finally {
      setIsTyping(false);
      scrollToBottom();
    }
  };

  return (
    <>
      <div className="ai-assistant-trigger" onClick={() => setIsOpen(!isOpen)} title="DevTracker Assistant">
        <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-robot'}`}></i>
      </div>

      {isOpen && (
        <div className="ai-popover chat-ui animate-up">
          <div className="chat-header">
            <h4>
              <i className="fa-solid fa-wand-sparkles" style={{color: 'var(--brand-accent)'}}></i> DevTracker AI
            </h4>
            <div className="chat-session-badge" title="End-to-End Encrypted Session">
              <i className="fa-solid fa-lock" style={{fontSize: '0.65rem'}}></i> {sessionId}
            </div>
          </div>
          
          <div className="chat-messages-container">
            {messages.length === 0 && (
              <div className="chat-welcome-msg">
                <i className="fa-solid fa-shield-halved"></i>
                <p>Messages are End-to-End Encrypted.<br/>Ask me about your DNA Score or stats.</p>
              </div>
            )}
            
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-bubble-wrapper ${msg.sender === 'user' ? 'user' : 'ai'}`}>
                {msg.sender === 'ai' && (
                  <div className="chat-avatar"><i className="fa-solid fa-robot"></i></div>
                )}
                <div className="chat-bubble">
                  {msg.text}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="chat-bubble-wrapper ai">
                <div className="chat-avatar"><i className="fa-solid fa-robot"></i></div>
                <div className="chat-bubble typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-area" onSubmit={handleSend}>
            <input 
              type="text" 
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask DevTracker AI..."
              autoFocus
            />
            <button type="submit" disabled={!inputText.trim() || isTyping}>
              <i className="fa-solid fa-paper-plane"></i>
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default DevTrackerAssistant;
